import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaguesApi } from '../api/leagues';
import { useAuth } from '../contexts/AuthContext';
import LeagueStatusBadge from '../components/LeagueStatusBadge';
import MatchCard from '../components/MatchCard';
import { Match } from '../api/matches';
import { teamsApi, TeamInvitation } from '../api/teams';
import { pairsApi, PairInvitation } from '../api/pairs';
import { seasonsApi, SeasonMatch, Season } from '../api/seasons';
import { useFeatures } from '../contexts/FeaturesContext';
import { useTranslation } from 'react-i18next';

const SEASON_STATUS_STYLE: Record<Season['status'], string> = {
  open: 'bg-green-100 text-green-700 border-green-300',
  active: 'bg-blue-100 text-blue-700 border-blue-300',
  finished: 'bg-gray-100 text-gray-500 border-gray-300',
  archived: 'bg-purple-100 text-purple-700 border-purple-300',
};

function MySeasons() {
  const { t } = useTranslation();
  const { data: seasons = [] } = useQuery({
    queryKey: ['seasons'],
    queryFn: () => seasonsApi.list().then((r) => r.data),
  });

  const mySeasons = seasons.filter((s) => s.is_registered);

  if (mySeasons.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>{t('dashboard.notRegisteredSeason')}</p>
        <Link to="/seasons" className="btn btn-primary btn-sm mt-4 inline-flex">
          {t('dashboard.browseSeasons')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {mySeasons.map((season) => (
        <Link
          key={season.id}
          to={`/seasons/${season.id}`}
          className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
        >
          <div>
            <h4 className="font-medium text-gray-900">{season.name}</h4>
            <p className="text-sm text-gray-500">
              {t('season.class')} {season.season_class}
            </p>
          </div>
          <span className={`badge border text-xs ${SEASON_STATUS_STYLE[season.status]}`}>
            {t(`season.status_${season.status}`)}
          </span>
        </Link>
      ))}
    </div>
  );
}

function MyLeagues() {
  const { t } = useTranslation();
  const { data: leagues = [] } = useQuery({
    queryKey: ['leagues'],
    queryFn: () => leaguesApi.list().then((r) => r.data),
  });

  const myLeagues = leagues.filter((l) => l.is_registered);

  if (myLeagues.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>{t('dashboard.notRegistered')}</p>
        <Link to="/leagues" className="btn btn-primary btn-sm mt-4 inline-flex">
          {t('dashboard.browseLeagues')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {myLeagues.map((league) => (
        <Link
          key={league.id}
          to={`/leagues/${league.id}`}
          className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
        >
          <div>
            <h4 className="font-medium text-gray-900">{league.name}</h4>
            {league.level && <p className="text-sm text-gray-500">{league.level}</p>}
          </div>
          <div className="flex items-center gap-3">
            <LeagueStatusBadge status={league.status} />
            {league.payment_status === 'pending' && (
              <span className="badge bg-yellow-100 text-yellow-700">{t('dashboard.paymentPending')}</span>
            )}
          </div>
        </Link>
      ))}
    </div>
  );
}

function PendingMatches({ leagueId }: { leagueId: number }) {
  const { user } = useAuth();
  const { data: matches = [] } = useQuery({
    queryKey: ['matches', 'league', leagueId],
    queryFn: () => leaguesApi.getMatches(leagueId).then((r) => r.data as Match[]),
  });

  const uid = user?.id;
  const pending = matches.filter((m) => {
    if (m.is_bye) return false;
    if (m.status !== 'pending' && m.status !== 'result_submitted') return false;
    if (!uid) return false;
    if (m.home_player_id === uid || m.away_player_id === uid) return true;
    const inHome = m.home_pair && (m.home_pair.player1?.id === uid || m.home_pair.player2?.id === uid);
    const inAway = m.away_pair && (m.away_pair.player1?.id === uid || m.away_pair.player2?.id === uid);
    return !!(inHome || inAway);
  });

  return (
    <>
      {pending.map((m) => (
        <MatchCard key={m.id} match={m} />
      ))}
    </>
  );
}

function PairInvitations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: invitations = [] } = useQuery({
    queryKey: ['pair-invitations-me'],
    queryFn: () => pairsApi.getMyInvitations().then((r) => r.data),
  });

  const acceptMutation = useMutation({
    mutationFn: (pairId: number) => pairsApi.accept(pairId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pair-invitations-me'] });
      queryClient.invalidateQueries({ queryKey: ['my-pairs'] });
    },
  });

  const declineMutation = useMutation({
    mutationFn: (pairId: number) => pairsApi.decline(pairId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pair-invitations-me'] }),
  });

  if (invitations.length === 0) return null;

  return (
    <div className="mb-2">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('dashboard.pairInvitations')}</h2>
      <div className="space-y-3">
        {(invitations as PairInvitation[]).map((inv) => (
          <div key={inv.id} className="p-4 rounded-lg border-2 border-green-300 bg-green-50 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-medium text-gray-900">{t('dashboard.invitedBy', { name: inv.inviter_name })}</p>
              <p className="text-sm text-gray-500">{t('dashboard.asDoublePartner')}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => declineMutation.mutate(inv.id)}
                disabled={declineMutation.isPending || acceptMutation.isPending}
                className="btn btn-secondary btn-sm"
              >
                {t('dashboard.decline')}
              </button>
              <button
                onClick={() => acceptMutation.mutate(inv.id)}
                disabled={acceptMutation.isPending || declineMutation.isPending}
                className="btn btn-primary btn-sm"
              >
                {acceptMutation.isPending ? '...' : t('dashboard.accept')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TeamInvitations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: invitations = [], isLoading } = useQuery({
    queryKey: ['team-invitations-me'],
    queryFn: () => teamsApi.getMyInvitations().then((r) => r.data),
  });

  const acceptMutation = useMutation({
    mutationFn: (teamId: number) => teamsApi.acceptInvitation(teamId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team-invitations-me'] }),
  });

  const declineMutation = useMutation({
    mutationFn: (teamId: number) => teamsApi.declineInvitation(teamId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team-invitations-me'] }),
  });

  if (isLoading || invitations.length === 0) return null;

  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">{t('dashboard.teamInvitations')}</h2>
      <div className="space-y-3">
        {(invitations as TeamInvitation[]).map((inv) => (
          <div key={inv.id} className="p-4 rounded-lg border-2 border-yellow-300 bg-yellow-50 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <p className="font-medium text-gray-900">{inv.team_name}</p>
              <p className="text-sm text-gray-500">{t('dashboard.invitedByTeam', { name: inv.captain_name })}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => declineMutation.mutate(inv.team_id)}
                disabled={declineMutation.isPending || acceptMutation.isPending}
                className="btn btn-secondary btn-sm"
              >
                {t('dashboard.decline')}
              </button>
              <button
                onClick={() => acceptMutation.mutate(inv.team_id)}
                disabled={acceptMutation.isPending || declineMutation.isPending}
                className="btn btn-primary btn-sm"
              >
                {acceptMutation.isPending ? '...' : t('dashboard.accept')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  pending: 'Offen',
  proposal_sent: 'Terminvorschlag',
  scheduled: 'Terminiert',
  result_pending: 'Ergebnis ausstehend',
};

const STATUS_COLOR: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-600',
  proposal_sent: 'bg-yellow-100 text-yellow-700',
  scheduled: 'bg-blue-100 text-blue-700',
  result_pending: 'bg-orange-100 text-orange-700',
};

function MySeasonMatches() {
  const { user } = useAuth();
  const { features } = useFeatures();

  const { data: matches = [] } = useQuery({
    queryKey: ['season-matches-mine'],
    queryFn: () => seasonsApi.getMyMatches().then(r => r.data),
    enabled: !!user && !!features?.saison,
  });

  const open = (matches as SeasonMatch[]).filter(m =>
    ['pending', 'proposal_sent', 'scheduled', 'result_pending'].includes(m.status)
  );

  if (!features?.saison || open.length === 0) return null;

  const grouped = open.reduce((acc, m) => {
    const key = m.season_id;
    if (!acc[key]) acc[key] = { name: m.season_name ?? `Saison #${m.season_id}`, matches: [] };
    acc[key].matches.push(m);
    return acc;
  }, {} as Record<number, { name: string; matches: SeasonMatch[] }>);

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Meine Saison-Matches</h2>
      <div className="space-y-4">
        {Object.entries(grouped).map(([seasonId, group]) => (
          <div key={seasonId} className="card">
            <div className="flex items-center justify-between mb-3">
              <p className="font-medium text-gray-900 text-sm">{group.name}</p>
              <Link to={`/seasons/${seasonId}`} className="text-xs text-primary-600 hover:text-primary-700">
                Zur Saison →
              </Link>
            </div>
            <div className="space-y-2">
              {group.matches.map(m => {
                const homeLabel = m.home_team?.name
                  ?? (m.home_pair ? `${m.home_pair.player1?.first_name ?? '?'} / ${m.home_pair.player2?.first_name ?? '?'}` : `${m.home_user?.first_name ?? '?'} ${m.home_user?.last_name ?? ''}`);
                const awayLabel = m.away_team?.name
                  ?? (m.away_pair ? `${m.away_pair.player1?.first_name ?? '?'} / ${m.away_pair.player2?.first_name ?? '?'}` : m.away_user ? `${m.away_user.first_name} ${m.away_user.last_name}` : '–');
                return (
                  <div key={m.id} className="flex items-center justify-between gap-2 py-2 border-b border-gray-100 last:border-0">
                    <p className="text-sm text-gray-800">
                      <span className="font-medium">{homeLabel}</span>
                      <span className="text-gray-400 mx-1">vs.</span>
                      <span className="font-medium">{awayLabel}</span>
                    </p>
                    <span className={`badge text-xs shrink-0 ${STATUS_COLOR[m.status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {STATUS_LABEL[m.status] ?? m.status}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { features } = useFeatures();

  const { data: leagues = [] } = useQuery({
    queryKey: ['leagues'],
    queryFn: () => leaguesApi.list().then((r) => r.data),
    enabled: features.liga,
  });

  const myActiveLeagues = leagues.filter(
    (l) => l.is_registered && ['active', 'knockout'].includes(l.status)
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-8">
        {t('dashboard.welcome', { name: user?.first_name })}
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Pair & Team invitations */}
          {(features.liga || features.saison) && <PairInvitations />}
          {(features.mannschaft || features.saison) && <TeamInvitations />}

          {/* Pending matches */}
          {features.liga && myActiveLeagues.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.pendingMatches')}</h2>
              <div className="space-y-3">
                {myActiveLeagues.map((l) => (
                  <PendingMatches key={l.id} leagueId={l.id} />
                ))}
              </div>
            </div>
          )}

          {/* Season matches */}
          <MySeasonMatches />

          {/* My seasons */}
          {features.saison && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.mySeasons')}</h2>
              <MySeasons />
            </div>
          )}

          {/* My leagues */}
          {features.liga && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.myLeagues')}</h2>
              <MyLeagues />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-3">{t('dashboard.myProfile')}</h3>
            <div className="flex items-center gap-3 mb-4">
              {user?.photo_url ? (
                <img
                  src={`${process.env.REACT_APP_API_URL ?? ''}${user.photo_url}`}
                  alt={t('nav.profilePhoto')}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-lg">
                  {user?.first_name[0]}{user?.last_name[0]}
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900">{user?.first_name} {user?.last_name}</p>
                {user?.level && <p className="text-sm text-gray-500">{user.level}</p>}
              </div>
            </div>
            <Link to="/profile" className="btn btn-secondary btn-sm w-full">
              {t('dashboard.editProfile')}
            </Link>
          </div>

          {features.liga && (
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-3">{t('dashboard.availableLeagues')}</h3>
              <p className="text-sm text-gray-500 mb-3">
                {t('dashboard.openRegistrations')}
              </p>
              {leagues.filter((l) => l.status === 'open' && !l.is_registered).length > 0 ? (
                <div className="space-y-2">
                  {leagues
                    .filter((l) => l.status === 'open' && !l.is_registered)
                    .slice(0, 3)
                    .map((l) => (
                      <Link
                        key={l.id}
                        to={`/leagues/${l.id}`}
                        className="block text-sm text-primary-600 hover:text-primary-700 font-medium"
                      >
                        {l.name}
                      </Link>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">{t('dashboard.noOpenRegistrations')}</p>
              )}
              <Link to="/leagues" className="btn btn-primary btn-sm w-full mt-3">
                {t('dashboard.allLeagues')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
